import { CalendarClock, CalendarX2, Infinity as SenzaFine, RotateCcw } from "lucide-react";
import { useState, type FormEvent } from "react";

import { dateKeyInZone } from "../../../shared/time-zone";
import { addDays, daysBetween } from "../../../shared/week";
import { trpc, type RouterOutputs } from "../../lib/trpc";

type Azienda = RouterOutputs["platform"]["companies"][number];

/** Lo stesso fuso del server: una data promessa al telefono vale in Italia. */
const FUSO_PROVA = "Europe/Rome";
const GIORNI_RAPIDI = [7, 15, 30] as const;

function oggi(now: Date) {
  return dateKeyInZone(now, FUSO_PROVA);
}

function giorni(quanti: number) {
  return quanti === 1 ? "1 giorno" : `${quanti} giorni`;
}

/**
 * La prova a parole, contata in giorni di calendario italiani e non in ore: "scade oggi"
 * deve voler dire oggi per chi la legge, anche alle undici di sera.
 */
export function descriviProva(access: Azienda["access"], now: Date = new Date()) {
  if (access.status === "suspended") return "Sospesa";
  if (access.status === "customer") return "Cliente";
  if (!access.trialEndsAt) return "Prova senza scadenza";
  const fine = new Date(access.trialEndsAt);
  const scadenza = dateKeyInZone(fine, FUSO_PROVA);
  if (access.blocked) {
    const da = daysBetween(scadenza, oggi(now));
    return da <= 0 ? "Prova scaduta oggi" : `Prova scaduta da ${giorni(da)}`;
  }
  const restano = daysBetween(oggi(now), scadenza);
  const data = fine.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric", timeZone: FUSO_PROVA });
  return restano <= 0 ? `Prova, scade oggi (${data})` : `Prova, restano ${giorni(restano)}: scade il ${data}`;
}

/**
 * La sezione Prova del quadrato: tutti i comandi della prova in un posto solo.
 *
 * Quello che si vede dipende dallo stato, perche lo stesso vale sul server: i comandi della
 * scadenza per una azienda in prova, Riporta in prova per una cliente, niente per una
 * sospesa, che prima si riattiva. Il server rifiuta comunque ogni altro caso: nascondere un
 * pulsante non impedisce a nessuno di chiamare l'API a mano.
 */
export function ProvaAzienda({ azienda }: { azienda: Azienda }) {
  const utils = trpc.useUtils();
  const aggiorna = { onSuccess: () => void utils.platform.invalidate() };
  const extend = trpc.platform.extendTrial.useMutation(aggiorna);
  const setEnd = trpc.platform.setTrialEnd.useMutation(aggiorna);
  const close = trpc.platform.closeTrial.useMutation(aggiorna);
  const removeEnd = trpc.platform.removeTrialEnd.useMutation(aggiorna);
  const convert = trpc.platform.convert.useMutation(aggiorna);
  const revert = trpc.platform.revertToTrial.useMutation({
    onSuccess: () => {
      setRiporta(false);
      void utils.platform.invalidate();
    },
  });
  const [riporta, setRiporta] = useState(false);
  const [avviso, setAvviso] = useState<string | null>(null);

  const { access } = azienda;
  const errore = (extend.error ?? setEnd.error ?? close.error ?? removeEnd.error ?? convert.error ?? revert.error)?.message;
  const scadenzaAttuale = access.trialEndsAt ? dateKeyInZone(new Date(access.trialEndsAt), FUSO_PROVA) : null;

  async function esegui(azione: () => Promise<unknown>) {
    setAvviso(null);
    try {
      await azione();
    } catch {
      // L'errore resta nella mutazione e la sezione lo mostra.
    }
  }

  function aggiungiGiorniLiberi(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // Dal campo vero, non dallo stato: la dettatura scrive senza avvisare React.
    const valore = String(new FormData(event.currentTarget).get("days") ?? "").trim();
    const quanti = Number(valore);
    if (!Number.isInteger(quanti) || quanti < 1 || quanti > 365) {
      setAvviso("Scrivi un numero intero di giorni, da 1 a 365.");
      return;
    }
    void esegui(() => extend.mutateAsync({ companyId: azienda.id, days: quanti }));
  }

  function cambiaData(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const giorno = String(new FormData(event.currentTarget).get("trialEndDay") ?? "");
    if (!giorno) {
      setAvviso("Scegli la nuova data di scadenza.");
      return;
    }
    void esegui(() => setEnd.mutateAsync({ companyId: azienda.id, trialEndDay: giorno }));
  }

  function riportaInProva(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const giorno = String(new FormData(event.currentTarget).get("trialEndDay") ?? "");
    if (!giorno) {
      setAvviso("Scegli la scadenza della prova.");
      return;
    }
    void esegui(() => revert.mutateAsync({ companyId: azienda.id, trialEndDay: giorno }));
  }

  function chiudiAdesso() {
    const conferma = window.confirm(
      `Chiudere adesso la prova di ${azienda.name}?\n\nChi entra vede subito la schermata di prova scaduta. I dati restano tutti al loro posto, e allungando la prova si rientra.`,
    );
    if (conferma) void esegui(() => close.mutateAsync({ companyId: azienda.id }));
  }

  return (
    <section className="console-prova" aria-label={`Prova di ${azienda.name}`}>
      <div className="console-prova__stato">
        <span className="eyebrow">Prova</span>
        <strong>{descriviProva(access)}</strong>
        {access.status === "trial" && !access.trialEndsAt && (
          <span className="console-stato console-stato--prova"><SenzaFine size={13} aria-hidden="true" /> Senza scadenza</span>
        )}
      </div>

      {access.status === "trial" && (
        <>
          <div className="console-prova__riga">
            <span className="console-prova__etichetta">Aggiungi giorni</span>
            {GIORNI_RAPIDI.map((quanti) => (
              <button
                key={quanti}
                type="button"
                className="button button--outline"
                onClick={() => void esegui(() => extend.mutateAsync({ companyId: azienda.id, days: quanti }))}
              >
                +{quanti}
              </button>
            ))}
            <form className="console-prova__campo" onSubmit={aggiungiGiorniLiberi}>
              <label>
                <span>Giorni</span>
                <input name="days" type="number" inputMode="numeric" min={1} max={365} step={1} placeholder="45" />
              </label>
              <button className="button button--outline">Aggiungi</button>
            </form>
          </div>
          <p className="form-hint">
            Si contano dalla scadenza se e ancora davanti, da oggi se e passata o non c'e.
          </p>
          <div className="console-prova__riga">
            <form className="console-prova__campo" onSubmit={cambiaData}>
              <label>
                <span>Nuova scadenza</span>
                <input name="trialEndDay" type="date" defaultValue={scadenzaAttuale ?? addDays(oggi(new Date()), 30)} />
              </label>
              <button className="button button--outline"><CalendarClock size={16} /> Cambia data</button>
            </form>
          </div>
          <div className="console-prova__riga">
            {access.trialEndsAt && (
              <button
                type="button"
                className="button button--ghost"
                onClick={() => void esegui(() => removeEnd.mutateAsync({ companyId: azienda.id }))}
              >
                <SenzaFine size={16} /> Togli la scadenza
              </button>
            )}
            <button type="button" className="button button--ghost" onClick={chiudiAdesso}>
              <CalendarX2 size={16} /> Chiudi la prova adesso
            </button>
            <button type="button" className="button button--primary" onClick={() => void esegui(() => convert.mutateAsync({ companyId: azienda.id }))}>
              Diventa cliente
            </button>
          </div>
        </>
      )}

      {access.status === "customer" && (
        <div className="console-prova__riga">
          {/* L'azione inversa della firma: serve per un clic sbagliato e per un ripensamento. */}
          {!riporta && (
            <button type="button" className="button button--outline" onClick={() => setRiporta(true)}>
              <RotateCcw size={16} /> Riporta in prova
            </button>
          )}
          {riporta && (
            <form className="console-prova__campo" onSubmit={riportaInProva}>
              <label>
                <span>Nuova scadenza della prova</span>
                <input name="trialEndDay" type="date" defaultValue={addDays(oggi(new Date()), 30)} />
              </label>
              <button className={`button button--primary${revert.isPending ? " button--saving" : ""}`}>
                {revert.isPending ? "Salvataggio..." : "Salva la nuova scadenza"}
              </button>
              <button type="button" className="button button--ghost" onClick={() => setRiporta(false)}>Annulla</button>
            </form>
          )}
        </div>
      )}

      {access.status === "suspended" && (
        <div className="console-prova__riga">
          <p className="form-hint">Sospesa: la prova si gestisce dopo averla riattivata.</p>
          <button type="button" className="button button--primary" onClick={() => void esegui(() => convert.mutateAsync({ companyId: azienda.id }))}>
            Diventa cliente
          </button>
        </div>
      )}

      {avviso && <p className="form-error">{avviso}</p>}
      {!avviso && errore && <p className="form-error">{errore}</p>}
    </section>
  );
}
